/**
 * API client.
 *
 * Access token lives in memory only (never localStorage) — see ARCHITECTURE.md
 * §3. The refresh token is an httpOnly cookie the browser sends automatically,
 * so a 401 triggers one silent refresh and a single retry.
 */

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    request_id?: string;
  };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const BASE = "/api/v1";

let accessToken: string | null = null;
export const setAccessToken = (t: string | null) => {
  accessToken = t;
};
export const getAccessToken = () => accessToken;

/** Single-flight refresh: N concurrent 401s must not fire N refreshes. */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Endpoints that must NOT trigger a refresh-and-retry on 401.
 *
 * Only these three — an earlier version excluded everything under /auth/,
 * which silently broke session restore: /auth/me is exactly the call that
 * needs to refresh after a page reload, when the in-memory token is gone but
 * the httpOnly cookie is still valid.
 */
const NO_REFRESH_PATHS = ["/auth/refresh", "/auth/login", "/auth/logout"];

async function refresh(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        setAccessToken(null);
        return false;
      }
      const data = (await res.json()) as { access_token: string };
      setAccessToken(data.access_token);
      return true;
    } catch {
      setAccessToken(null);
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

async function toError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> = {};
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    /* non-JSON error (proxy, gateway) — fall through to defaults */
  }
  return new ApiError(
    res.status,
    body.error?.code ?? "unknown_error",
    body.error?.message ?? res.statusText ?? "Request failed",
    body.error?.details ?? {},
    body.error?.request_id,
  );
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<T> {
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });

  if (res.status === 401 && retry && !NO_REFRESH_PATHS.includes(path)) {
    if (await refresh()) return apiFetch<T>(path, init, false);
  }

  if (!res.ok) throw await toError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Fetch a binary response and hand it to the browser as a download.
 *
 * A plain `<a href download>` cannot work here: the access token lives in JS
 * memory (never localStorage — see ARCHITECTURE.md §3), so the browser has no
 * way to attach the Authorization header and the request 401s. Fetching it
 * ourselves keeps the auth model intact and still gives the user a file.
 */
export async function downloadFile(
  path: string,
  fallbackName: string,
  retry = true,
): Promise<void> {
  const headers = new Headers();
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);

  const res = await fetch(`${BASE}${path}`, { headers, credentials: "include" });

  if (res.status === 401 && retry && (await refresh())) {
    return downloadFile(path, fallbackName, false);
  }
  if (!res.ok) throw await toError(res);

  // Prefer the server's filename — it carries the report/ticket number.
  const disposition = res.headers.get("content-disposition") ?? "";
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const plain = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
  const name = utf8 ? decodeURIComponent(utf8) : (plain ?? fallbackName);

  const blobUrl = URL.createObjectURL(await res.blob());
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick; revoking synchronously can cancel the download.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
}

/**
 * Fetch a binary resource (e.g. a profile photo) with auth and return an
 * object URL suitable for an <img src>. Same reason as downloadFile: the token
 * is in memory, so a plain <img src="/api/…"> would 401. The caller must
 * URL.revokeObjectURL() the result when the element unmounts.
 */
export async function fetchBlobUrl(path: string, retry = true): Promise<string> {
  const headers = new Headers();
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  const res = await fetch(`${BASE}${path}`, { headers, credentials: "include" });
  if (res.status === 401 && retry && (await refresh())) {
    return fetchBlobUrl(path, false);
  }
  if (!res.ok) throw await toError(res);
  return URL.createObjectURL(await res.blob());
}

export const api = {
  get: <T>(p: string) => apiFetch<T>(p),
  post: <T>(p: string, body?: unknown) =>
    apiFetch<T>(p, { method: "POST", body: JSON.stringify(body ?? {}) }),
  patch: <T>(p: string, body?: unknown) =>
    apiFetch<T>(p, { method: "PATCH", body: JSON.stringify(body ?? {}) }),
  put: <T>(p: string, body?: unknown) =>
    apiFetch<T>(p, { method: "PUT", body: JSON.stringify(body ?? {}) }),
  delete: <T>(p: string) => apiFetch<T>(p, { method: "DELETE" }),
  // FormData is passed through untouched: apiFetch skips the JSON
  // Content-Type when the body is FormData, so the browser sets the
  // multipart boundary itself.
  upload: <T>(p: string, form: FormData) =>
    apiFetch<T>(p, { method: "POST", body: form }),
};
